import { Pipe, PipeTransform } from "@angular/core";
import Moment from "moment";

import { AssemblyConfig } from "./sharedinterfaces";

@Pipe({ name: "hilevel" })
export class HiLevelPipe implements PipeTransform {
	transform(value: any): any {
		if (value !== null && typeof value === "number") {
			if (value >= AssemblyConfig.HumsHiThreshold.Alarm) {
				return 4;
			} else if (value >= AssemblyConfig.HumsHiThreshold.Warning) {
				return 3;
			} else {
				return 1;
			}
		} else {
			return null;
		}
	}
}

@Pipe({ name: "componentname" })
export class ComponentnamePipe implements PipeTransform {
	transform(value: any): any {
		switch (value) {
			case "tach":
				return "Tach";
			case "shaft":
				return "Shaft";
			case "gear":
				return "Gear";
			case "bearing":
				return "Bearing";
			case "tracker":
				return "Tracker";
			case "flutter":
				return "Flutter";
			case "engine":
				return "Engine";
			case "sensor":
				return "Sensor";
		}
	}
}

const units = [
	"B",
	"KB",
	"MB",
	"GB",
	"TB",
	"PB"
];
@Pipe({ name: "filesize" })
export class FilesizePipe implements PipeTransform {
	transform(bytes: any, precision: number): any {
		if (isNaN(parseFloat(bytes)) || !isFinite(bytes)) {
			return "";
		}

		let unit = 0;

		while (bytes >= 1024) {
			bytes /= 1024;
			unit++;
		}
		if (unit === 0) {
			precision = 0;
		}
		return bytes.toFixed(precision) + " " + units[unit];
	}
}

@Pipe({ name: "httpstatuscolor" })
export class HttpstatuscolorPipe implements PipeTransform {
	transform(status: number): any {
		if (status >= 500) { return "#F55"; }
		if (status === 404) { return "#A50"; }
		if (status >= 400) { return "#FF5"; }
		if (status === 304) { return "#0AA"; }
		if (status >= 300) { return "#5FF"; }
		if (status === 200) { return "#0A0"; }
		if (status >= 200) { return "#5F5"; }
		return "#F5F";
	}
}

@Pipe({ name: "gonedarkcolor" })
export class GonedarkcolorPipe implements PipeTransform {
	transform(latedays: number, gonedarkdays: number): any {
		if (gonedarkdays === 0) return "#CCC";
		if (latedays < gonedarkdays) {
			return "#5F5";
		} else if (latedays === gonedarkdays) {
			return "#F00";
		} else {
			return "#F55";
		}
	}
}

@Pipe({ name: "sentemailstatuscolor" })
export class SentEmailStatusColorPipe implements PipeTransform {
	transform(status: string): any {
		if (status === "complained") { return "#F0F"; }
		if (status === "rejected") { return "#F0F"; }
		if (status === "bounced") { return "#F00"; }
		if (status === "sent") { return "#FF0"; }
		if (status === "delivered") { return "#0F0"; }
		return undefined;
	}
}

@Pipe({ name: "datetime" })
export class DatetimePipe implements PipeTransform {
	transform(moment: Moment.Moment | string | Date, format: string, offset?: number): any {
		if (!moment) {
			return "";
		}
		moment = Moment(moment);
		if (format === "local") {
			return moment.local().format("MMM D, YYYY, h:mm:ss A ZZ");
		} else if (format === "localshort") {
			return moment.local().format("MM/DD/YY H:mm:ss");
		} else if (format === "time") {
			if (offset) {
				return moment.utcOffset(offset * 60).format("H:mm:ss");
			} else {
				return moment.utc().format("H:mm:ss");
			}
		} else if (format === "date") {
			return moment.utc().format("MMM D, YYYY");
		} else if (format === "offset") {
			if (offset) {
				return moment.utcOffset(offset * 60).format("MMM D, YYYY, HH:mm:ss" + " (UTC " + (offset < 0 ? "" : "+") + offset + ")");
			} else {
				return moment.utc().format("MMM D, YYYY, HH:mm:ss" + " (UTC)");
			}
		} else if (format === "offsetshort") {
			if (offset) {
				return moment.utcOffset(offset * 60).format("YYYY-MM-DD HH:mm");
			} else {
				return moment.utc().format("YYYY-MM-DD HH:mm");
			}
		} else {
			return moment.utc().format("MMM D, YYYY, HH:mm:ss" + " (UTC)");
		}
	}
}

@Pipe({ name: "runtime" })
export class RuntimePipe implements PipeTransform {
	transform(seconds: number, prefix?: string): any {
		if (!seconds) {
			return "";
		}
		if (!prefix) {
			prefix = "";
		}
		const duration = Moment.duration(seconds, "seconds");
		if (duration.asSeconds() < 60) {
			return prefix + duration.asSeconds().toFixed(0) + " sec";
		} else if (duration.asMinutes() < 20) {
			return prefix + duration.asMinutes().toFixed(1) + " min";
		} else {
			return prefix + duration.asHours().toFixed(1) + " hrs";
		}
	}
}

@Pipe({ name: "duration" })
export class DurationPipe implements PipeTransform {
	transform(seconds: number): any {
		if (!seconds) {
			return "";
		}
		const duration = Moment.duration(seconds, "seconds");
		return Moment.utc(duration.as("milliseconds")).format("HH:mm:ss");
	}
}

@Pipe({ name: "roundlist" })
export class RoundListPipe implements PipeTransform {
	transform(values: number[], decimals: number): any {
		return values.map(item => item.toFixed(decimals)).join(", ");
	}
}

@Pipe({ name: "commalist" })
export class CommaListPipe implements PipeTransform {
	transform(list: string): any {
		if (list) {
			return list.split(",").join(", ");
		} else {
			return "";
		}
	}
}

@Pipe({ name: "ifempty" })
export class IfEmptyPipe implements PipeTransform {
	transform(item: any, replacement: any, suffix: any = ""): any {
		if (item === undefined || item === null || item === "") {
			return replacement;
		} else {
			return item + suffix;
		}
	}
}

@Pipe({ name: "isnotghost" })
export class IsNotGhostPipe implements PipeTransform {
	transform(items: Array<any>): Array<any> {
		return items.filter(item => !item.altid);
	}
}

@Pipe({ name: "truncatedfile" })
export class TruncatedFilePipe implements PipeTransform {
	transform(value: string): string {
		return /[^/]*$/.exec(value)[0];
	}
}

@Pipe({ name: "shortexception" })
export class ShortExceptionPipe implements PipeTransform {
	transform(value: string): string {
		let message = value;
		try {
			const obj = JSON.parse(value);
			if (obj.message) {
				message = obj.message;
			}
		} catch {
			// We'll return nothing later
		}
		if (message) {
			return message.split("\n")[0].substring(0, 30);
		} else {
			return "";
		}

	}
}

@Pipe({ name: "removeBaseline" })
export class RemoveBaselinePipe implements PipeTransform {
	transform(value: any): any {
		if (value && value.toLowerCase().startsWith("baseline")) {
			return value.substring(8).trim();
		}
		return value;
	}
}
